import { photoQualityWarning, supportedTeachingPhoto, teachingPhotoAccept } from './photoQuality';
import { useEffect, useState } from 'react';

/** Preview is local; only explicit Add uploads into this learner's private response. */
export function AssignmentPhotoPicker({
  onAdd,
  disabled,
}: {
  onAdd: (file: File, rotation?: number) => Promise<unknown>;
  disabled: boolean;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState('');
  const [error, setError] = useState('');
  const [rotation, setRotation] = useState(0);
  const [warning, setWarning] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const url = file ? URL.createObjectURL(file) : '';
    setPreview(url);
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [file]);
  function choose(next: File | undefined) {
    if (!next) return;
    if (!supportedTeachingPhoto(next) || next.size > 8 * 1024 * 1024 || next.size === 0) {
      setError('Use a JPEG, PNG, WebP, AVIF or HEIC photo up to 8 MB.');
      return;
    }
    setError('');
    setRotation(0);
    setWarning('');
    setFile(next);
  }
  return (
    <section className="space-y-3">
      <h3 className="font-bold">Photograph your working</h3>
      <label className="block">
        <span className="field-label">Take a homework photo</span>
        <input
          type="file"
          className="block max-w-full"
          accept={teachingPhotoAccept}
          capture="environment"
          disabled={disabled || busy}
          onChange={(e) => {
            choose(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
      </label>
      <label className="block">
        <span className="field-label">Choose a homework photo</span>
        <input
          type="file"
          className="block max-w-full"
          accept={teachingPhotoAccept}
          disabled={disabled || busy}
          onChange={(e) => {
            choose(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
      </label>
      <p className="text-sm text-slate2">
        Up to 8 photos per response, 8 MB each. Check that all working is readable. This preview
        stays on your device until you add it. HEIC is converted privately after Add. Your teacher
        sees it only after submission; linked parents see published work.
      </p>
      {file && (
        <div className="space-y-3 rounded-2xl border border-hairline p-4">
          <p className="break-words">{file.name}</p>
          {preview && (
            <img
              className="max-h-96 max-w-full rounded-xl object-contain"
              src={preview}
              style={{
                display: warning.startsWith('This browser cannot preview') ? 'none' : undefined,
                transform: `rotate(${rotation}deg)`,
                maxHeight: 220,
                maxWidth: '70%',
                margin: '44px auto',
              }}
              onLoad={(e) => setWarning(photoQualityWarning(e.currentTarget))}
              onError={() =>
                setWarning(
                  'This browser cannot preview this photo. HEIC is converted privately after Add; inspect the uploaded preview before submitting.',
                )
              }
              alt="Homework photo before upload"
            />
          )}
          {warning && (
            <p role="status" className="text-sm text-slate2">
              {warning}
            </p>
          )}
          <p className="text-xs text-slate2">Extra rotation: {rotation}°</p>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              className="btn-pill-ghost"
              disabled={disabled || busy}
              onClick={() => setRotation((old) => (old + 90) % 360)}
            >
              Rotate photo 90°
            </button>
            <button
              type="button"
              className="btn-pill-secondary"
              disabled={disabled || busy}
              onClick={() => {
                setError('');
                setBusy(true);
                void onAdd(file, rotation)
                  .then(() => setFile(null))
                  .catch(() => setError('Upload failed. Your photo is retained; retry when ready.'))
                  .finally(() => setBusy(false));
              }}
            >
              Add photo to private response
            </button>
            <button
              type="button"
              className="btn-pill-ghost"
              disabled={disabled || busy}
              onClick={() => setFile(null)}
            >
              Remove / retake photo
            </button>
          </div>
        </div>
      )}
      {error && <p role="alert">{error}</p>}
    </section>
  );
}
